import React, { useEffect, useState } from 'react';
import { Button, Col, Placeholder, Row } from '@thd-olt-component-react/core-ui';
import { useNavigate } from '@thd-olt-component-react/router';
import { useConfigService } from '@thd-nucleus/experience-context';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { ServerErrorMessage } from '../components/common/ErrorMessage/ErrorMessage';
import { isServerError } from '../utils/validations';
import PageHeader from '../components/common/PageHeader/PageHeader';
import { useStore } from '../store/index';
import { clickEvents, pageLoadEvents, errorEvents } from '../utils/analytics';
import LoaderTextSkeleton from '../components/common/LoaderTextSkeleton/LoaderTextSkeleton';
import TutorialOverlay from './Overlays/TutorialOverlay';
import JobSeekersPod from '../components/Home/JobSeekersPod';
import FindTalentSection from '../components/Home/FindTalentSection';
import TermsAndConditions from '../components/common/TermsAndConditions/TermsAndConditions';
import { LandingPageHelpText } from '../components/common/LandingPageHelpText/LandingPageHelpText';
import { Notification } from '../components/NotificationsProvider/Notification';
import { NotificationsProvider } from '../components/NotificationsProvider/NotificationsProvider';
import { EXP_ROUTES } from '../utils/constants';
import { generateImageSource } from '../utils/generateImageSource';
import SplashPage from '../components/Home/SplashPage';

export const Home = observer(() => {
  const [tutorialOpened, setTutorialOpened] = useState(false);

  const NewSplashPage = useConfigService('fs:NewSplashPage');

  const {
    authStore: { authorized, authInProgress, errorCode, initAuth },
    filterStore: { addFilterParam }
  } = useStore();
  const navigate = useNavigate();

  const findCandidateContent = {
    header: 'Path To Pro Candidates',
    errorHeader: 'This Feature is Unavailable',
    text: 'Are you looking for:'
  };

  useEffect(() => {
    pageLoadEvents.landingPage();

    // authorize and send error analytics as callback if not authorized
    initAuth(errorEvents.postPageError);

    if (document.referrer.includes('b2b/account/view/landingpage')) {
      pageLoadEvents.landingPageReferFromMyAccount();
    } else if (document.referrer.includes('myaccount/profile')) {
      pageLoadEvents.landingPageReferFromMyAccountSideBar();
    }
  }, []);

  const renderFindFulltimeCandidatesButton = () => {
    return (
      <Button
        data-testid="find-candidates-btn"
        className="p2p-landing-button"
        onClick={() => {
          addFilterParam('employmentTypes', 'Full Time');
          clickEvents.landingPageLinkClick('Full-Time Labor');
          navigate(EXP_ROUTES.CANDIDATE_SEARCH);
        }}
      >
        Full-Time Labor
      </Button>
    );
  };

  const renderFindParttimeCandidatesButton = () => {
    return (
      <Button
        data-testid="find-candidates-btn"
        className="p2p-landing-button"
        onClick={() => {
          addFilterParam('employmentTypes', 'Part Time');
          clickEvents.landingPageLinkClick('Part-Time Labor');
          navigate(EXP_ROUTES.CANDIDATE_SEARCH);
        }}
      >
        Part-Time Labor
      </Button>
    );
  };

  const renderFindShorttermCandidatesButton = () => {
    return (
      <Button
        data-testid="find-candidates-btn"
        className="p2p-landing-button"
        onClick={() => {
          addFilterParam('employmentTypes', 'Contractor');
          clickEvents.landingPageLinkClick('find candidates');
          navigate(EXP_ROUTES.CANDIDATE_SEARCH);
        }}
      >
        Short-Term Labor
      </Button>
    );
  };

  const renderButtons = () => {
    return (
      <>
        <Row>
          <Col xs="0" />
          <Col xs="12">{renderFindFulltimeCandidatesButton()}</Col>
          <Col xs="0" />
        </Row>
        <Row>
          <Col xs="0" />
          <Col xs="12">{renderFindParttimeCandidatesButton()}</Col>
          <Col xs="0" />
        </Row>
        <Row>
          <Col xs="0" />
          <Col xs="12">{renderFindShorttermCandidatesButton()}</Col>
          <Col xs="0" />
        </Row>
      </>
    );
  };

  const SplashPageA = () => {
    return (
      <>
        <div className="ptp-hidden__sm-up">
          <span>
            <img
              className="landingBanner__mobile-header"
              alt="find-candidate"
              src={generateImageSource('/images/v1/path-to-pro/PathtoProLogo.svg')}
            />
          </span>
        </div>
        <div className="ptp-hidden__xs-down">
          <PageHeader>Path to Pro Network</PageHeader>
        </div>
        <Row className="landingBanner">
          <ErrorBoundary name="landing-banner">
            <Col md={4} sm={4} xs={12} className="landingBanner__content">
              <span className="ptp-hidden__xs-down">
                <img
                  className="landingBanner__logo"
                  alt="find-candidate"
                  src={generateImageSource('/images/v1/path-to-pro/PathtoProLogo.svg')}
                />
              </span>
              <h1 tabIndex={-1} data-testid="landing-page-title" className="landingBanner__header">
                FIND LABOR.
                <br />
                HIRE FASTER.
              </h1>
              <section className="landingBanner__text">
                Hire skilled workers from our list of candidates on the Path to Pro Network.
              </section>
              {authorized ? (
                <Button
                  outline
                  data-testid="see-how-it-works-btn"
                  style={{ maxWidth: '260px' }}
                  className="p2p-landing-button"
                  onClick={() => {
                    clickEvents.landingPageLinkClick('see how it works');
                    setTutorialOpened(true);
                  }}
                >
                  See How It Works
                </Button>
              ) : (
                <Button
                  data-testid="link-to-path-to-pro"
                  onClick={() => {
                    window.open('https://www.pathtopro.com/');
                  }}
                >
                  Learn More at PathToPro.com
                </Button>
              )}
            </Col>
            <Col md={8} sm={8} xs={12} className="landingBanner__picture" />
          </ErrorBoundary>
        </Row>
        <Row>
          <ErrorBoundary name="path-to-pro">
            <Col md={6} sm={6} xs={12}>
              <main className="slm-find-candidate-block__root">
                <h2 className="slm-find-candidate-block__header">
                  {authInProgress ? (
                    <Placeholder
                      type="text"
                      height="32px"
                      width="45%"
                      className="p2p-skeleton__text p2p-skeleton__text--landing-block-header"
                    />
                  ) : (
                    findCandidateContent.header
                  )}
                </h2>
                <section className="slm-find-candidate-block__row">
                  <img
                    alt="find-candidate"
                    src="https://assets.thdstatic.com/images/v1/path-to-pro/construction-group-splash.png"
                    className="slm-find-candidate-block__circle-col"
                  />
                  <section className="slm-find-candidate-block__content-col">
                    <section className="slm-find-candidate-block__content-col-text">
                      {renderContentSection()}
                      {authorized && renderButtons()}
                      <LandingPageHelpText />
                    </section>
                  </section>
                </section>

                <section className="slm-find-candidate-block__row" />
              </main>
            </Col>
          </ErrorBoundary>

          <ErrorBoundary name="what-is-it-block">
            <Col md={6} sm={6} xs={12}>
              <div className="whatIsItBlock">
                <h2 className="slm-find-candidate-block__header">So What Is It?</h2>
                <p className={clsx('p', 'p__header')}>Find Skilled Laborers</p>
                <p className="p">
                  The Path to Pro Network gives you the ability to connect with the skilled labor
                  your business needs. Whether you need a short-term subcontractor to help take on
                  the next job or backfill that longtime veteran on your staff, inside you'll find
                  candidates by trade, location, and employment type. See a candidate's work
                  portfolio, experience level, and qualifications all in one place.
                </p>
                <p className="p">
                  To learn more about the program, visit{' '}
                  <a target="_blank" rel="noreferrer" href="https://www.pathtopro.com/">
                    PathToPro.com.
                  </a>
                  <br /><br />
                  <b>¿Hablas español?</b> Accede a los recursos{' '}
                  <a
                    href="https://www.pathtopro.com/resources-library/"
                  >
                    aquí
                  </a>
                </p>
                <br />
                <TermsAndConditions />
              </div>
            </Col>
          </ErrorBoundary>
        </Row>
        <ErrorBoundary name="find-talent-section">
          <FindTalentSection authorized={!authInProgress && authorized} />
        </ErrorBoundary>
        <ErrorBoundary name="job-seekers-pod">
          <JobSeekersPod />
        </ErrorBoundary>
        <ErrorBoundary name="tutorial-overlay">
          <TutorialOverlay isOpen={tutorialOpened} onClose={() => setTutorialOpened(false)} />
        </ErrorBoundary>
        <ErrorBoundary name="notification">
          <Notification />
        </ErrorBoundary>
      </>
    );
  };

  if (authInProgress) {
    return (
      <ErrorBoundary name="home-page">
        <NotificationsProvider>
          <div />
        </NotificationsProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary name="home-page">
      <NotificationsProvider>
        {NewSplashPage ? <SplashPage /> : <SplashPageA />}
      </NotificationsProvider>
    </ErrorBoundary>
  );

  function renderContentSection() {
    if (authInProgress) {
      return <LoaderTextSkeleton />;
    }
    if (isServerError(errorCode)) {
      return <ServerErrorMessage />;
    }
    return (
      <>
        <h2 className="slm-find-candidate-block__center">
          {findCandidateContent.text}
        </h2>
      </>
    );
  }
});
